import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { adminApi } from "@/api/client";
import { trackTiming } from "@/utils/observability";

export function usePermissionsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "me", "permissions"],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const started = performance.now();
      const { data, error, response } = await adminApi.GET(
        "/admin/v1/me/permissions"
      );
      if (!data?.permissions) {
        throw new Error(
          `permissions: ${response.status} ${JSON.stringify(error)}`
        );
      }
      trackTiming("permissions", performance.now() - started);
      return data.permissions;
    },
  });
}

export function useEnumsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "enums"],
    enabled,
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const { data, error, response } = await adminApi.GET("/admin/v1/enums");
      if (!data) {
        throw new Error(`enums: ${response.status} ${JSON.stringify(error)}`);
      }
      return data;
    },
  });
}

export function useMeQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "me"],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error, response } = await adminApi.GET("/admin/v1/me");
      if (!data) {
        throw new Error(`me: ${response.status} ${JSON.stringify(error)}`);
      }
      return data;
    },
  });
}

export function useRouteAccess(
  required: readonly string[],
  permissions: string[] | undefined
): "loading" | "allow" | "deny" {
  return useMemo(() => {
    if (permissions === undefined) return "loading";
    if (permissions.includes("*")) return "allow";
    if (!required.length) return "allow";
    const ok = required.some((p) => permissions.includes(p));
    return ok ? "allow" : "deny";
  }, [permissions, required]);
}
